import { useCallback, useEffect, useMemo, useState } from "react";
import { Package, Eye, Smartphone } from "lucide-react";
import { Asset } from "../../types/supplier";
import apiService from "../../services/apiService";
import { AssetDetailSidebar } from "../purchaseOrder/AssetDetailSidebar";

interface AssetsTabProps {
  supplierID: string;
}

interface ApiResponse<T> {
  data: T;
  status?: number;
  message?: string;
}

type AssetRow = Asset & { raw: Record<string, any> };

const PAGE_SIZE = 25;

const inr = (value: number | null | undefined) =>
  "₹" +
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const extractInvoicesList = (response: unknown): any[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  const root = response as Record<string, unknown>;
  const payload = root.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const nested = payload as Record<string, unknown>;
    if (Array.isArray(nested.invoices)) return nested.invoices;
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.rows)) return nested.rows;
  }
  return [];
};

const mapAsset = (raw: Record<string, any>, supplierID: string): AssetRow => ({
  id: String(raw.id ?? ""),
  assetId: String(raw.unique_asset_id ?? ""),
  assetType: String(raw.asset_type || "other"),
  poId: String(raw.po_id ?? ""),
  invoiceId:
    raw.invoice_id != null
      ? String(raw.invoice_id)
      : raw.invoiceId != null
      ? String(raw.invoiceId)
      : undefined,
  assetLineId: raw.asset_line_id != null ? String(raw.asset_line_id) : undefined,
  leaseStatus: "Active",
  supplierId: supplierID,
  primaryImageUrl: raw.primaryImageUrl ?? null,
  serialNumber: raw.serial_number ?? "",
  assetName: String(raw.item || "-"),
  specification: raw.specification ?? "",
  qty: toNumber(raw.qty) || 1,
  unitPrice: toNumber(raw.unit_price),
  taxRate: toNumber(raw.tax_rate),
  insuranceEndDate: raw.insurance_end_date ?? null,
  status: String(raw.status || "pending"),
  entity_name: raw.entity_name ?? "",
  raw,
});

export function AssetsTab({ supplierID }: AssetsTabProps) {
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [selectedPo, setSelectedPo] = useState<any>(null);
  const [supplier, setSupplier] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [assetsCategories, setAssetsCategories] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);

  useEffect(() => {
    const fetchAssets = async () => {
      if (!supplierID) {
        setAssets([]);
        setCurrentPage(1);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await apiService.get<ApiResponse<Record<string, any>[]>>(
          `/supplier/get-individual-assets-by-supplier-id/${supplierID}`
        );
        if (response.status === 200 && Array.isArray(response.data)) {
          setAssets(response.data.map((a) => mapAsset(a, supplierID)));
        } else {
          setAssets([]);
        }
        setCurrentPage(1);
      } catch {
        setAssets([]);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };

    void fetchAssets();
  }, [supplierID]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.get<ApiResponse<any[]>>(
          `/purchase-order/get-asset-categories-list`
        );
        setAssetsCategories(Array.isArray(response.data) ? response.data : []);
      } catch {
        setAssetsCategories([]);
      }
    };
    void fetchCategories();
  }, []);

  const getPOById = async (poId: string) => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-purchase-order-by-invoiced-id/${poId}`
      );
      return response?.data ?? null;
    } catch {
      return null;
    }
  };

  const handleAssetViewDetails = async (asset: AssetRow) => {
    const invoiceId =
      asset.invoiceId ??
      (asset.raw.invoice_id != null ? String(asset.raw.invoice_id) : undefined) ??
      (asset.raw.invoiceId != null ? String(asset.raw.invoiceId) : undefined);

    const sidebarAsset = {
      ...asset.raw,
      id: asset.raw.id ?? asset.id,
      invoice_id: invoiceId ?? asset.raw.invoice_id ?? null,
      invoiceId: invoiceId ?? asset.raw.invoiceId ?? null,
      invoice_number:
        asset.raw.invoice_number ??
        asset.raw.invoiceNumber ??
        asset.raw.invoice_no ??
        null,
      primaryImageUrl: asset.primaryImageUrl ?? asset.raw.primaryImageUrl ?? null,
    };

    const fallbackPo = {
      id: asset.poId,
      purchaseOrderCode: asset.poId ? `PO-${asset.poId}` : "—",
      supplierId: supplierID,
      customerId: undefined as string | undefined,
    };

    setSelectedAsset(sidebarAsset);
    setSelectedPo(fallbackPo);
    setSupplier(null);
    setCustomer(null);
    setInvoices([]);
    setAssetOpen(false);
    requestAnimationFrame(() => setAssetOpen(true));

    const poId = String(asset.poId || asset.raw.po_id || "").trim();
    if (!poId) return;

    const poData = await getPOById(poId);
    const resolvedPo = poData
      ? {
          ...poData,
          id: poData.id ?? poId,
          customerId: poData.customerId ?? poData.customer_id,
          supplierId: poData.supplierId ?? poData.supplier_id ?? supplierID,
          purchaseOrderCode:
            poData.purchaseOrderCode ??
            poData.purchase_order_code ??
            poData.po_number ??
            `PO-${poId}`,
          noa_id: poData.noa_id ?? poData.noaId,
          noaId: poData.noaId ?? poData.noa_id,
        }
      : fallbackPo;

    setSelectedPo(resolvedPo);
    setSelectedAsset((prev: any) =>
      prev
        ? {
            ...prev,
            customer_id: resolvedPo.customerId ?? prev.customer_id,
            noa_id: resolvedPo.noa_id ?? resolvedPo.noaId ?? prev.noa_id,
            po_id: resolvedPo.id ?? prev.po_id,
            purchase_order_code:
              resolvedPo.purchaseOrderCode ?? prev.purchase_order_code,
            invoice_id: prev.invoice_id ?? invoiceId ?? null,
          }
        : prev
    );

    const resolvedSupplierId = String(resolvedPo.supplierId ?? supplierID);
    const resolvedCustomerId = resolvedPo.customerId;

    try {
      const invoiceLookupId = resolvedPo.id ?? poId;
      if (invoiceLookupId) {
        const invRes = await apiService.get<any>(
          `/purchase-order/get-invoice-by-po-id/${invoiceLookupId}`
        );
        const invList = extractInvoicesList(invRes);
        setInvoices(invList);

        if (invoiceId && invList.length > 0) {
          const matched = invList.find((i: any) => String(i.id) === String(invoiceId));
          const num = matched?.number || matched?.invoiceNumber || matched?.invoice_number;
          setSelectedAsset((prev: any) =>
            prev && num
              ? {
                  ...prev,
                  invoice_number: num,
                  invoiceNumber: num,
                }
              : prev
          );
        }
      }
    } catch {
      // silent
    }

    try {
      const [supplierRes, customerRes] = await Promise.all([
        apiService.get<any>(`/supplier/get-supplier-by-id/${resolvedSupplierId}`),
        resolvedCustomerId
          ? apiService.get<any>(`/purchase-order/customer-name/${resolvedCustomerId}`)
          : Promise.resolve(null),
      ]);

      setSupplier(supplierRes?.data ?? supplierRes?.supplier ?? null);
      setCustomer(customerRes?.data ?? null);
    } catch {
      // silent
    }
  };

  const handleUpdateAssetDetails = useCallback(
    (updated: any) => {
      setSelectedAsset((prev: any) =>
        prev && String(prev.id) === String(updated.id) ? { ...prev, ...updated } : prev
      );
      setAssets((prev) =>
        prev.map((row) => {
          if (String(row.id) !== String(updated.id)) return row;
          return mapAsset({ ...row.raw, ...updated }, supplierID);
        })
      );
    },
    [supplierID]
  );

  const handlePrimaryImageChange = useCallback((assetId: string, imageUrl: string) => {
    setSelectedAsset((prev: any) =>
      prev && String(prev.id) === String(assetId)
        ? { ...prev, primaryImageUrl: imageUrl }
        : prev
    );
    setAssets((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(assetId)) return row;
        return {
          ...row,
          primaryImageUrl: imageUrl,
          raw: { ...row.raw, primaryImageUrl: imageUrl },
        };
      })
    );
  }, []);

  const totalPages = Math.max(1, Math.ceil(assets.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedAssets = useMemo(() => {
    return assets.slice(startIndex, startIndex + PAGE_SIZE);
  }, [assets, startIndex]);

  return (
    <div className="bg-white border border-[#dde3e4] rounded-[10px] p-5 shadow-[0_1px_2px_rgba(21,27,29,0.04),0_4px_10px_-6px_rgba(21,27,29,0.10)] font-['Poppins',sans-serif]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-[8px] bg-[#F1F6FA] text-[#2e6b76] flex items-center justify-center font-semibold text-xs flex-shrink-0">
          <Package className="w-4 h-4" />
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-[#151b1d]">
            Assets supplied
          </h2>
          <span className="text-[10.5px] bg-[#f6f8f8] text-[#151b1d] px-1.5 py-0.2 rounded font-medium">
            {assets.length}
          </span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-56 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-[#dde3e4] border-t-[#3f8b98]" />
        </div>
      ) : assets.length > 0 ? (
        <div className="border border-[#dde3e4] rounded-[8px] overflow-hidden bg-white">
          <div className="overflow-x-auto max-h-[560px]">
            <table className="w-full border-collapse text-left min-w-[900px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#F1F6FA] text-[#2e6b76] border-b border-[#dde3e4] text-[11px] font-semibold">
                  <th className="py-3 px-3.5 pl-4">Asset ID</th>
                  <th className="py-3 px-3.5">Company name</th>
                  <th className="py-3 px-3.5">Asset name</th>
                  <th className="py-3 px-3.5 text-right">Qty</th>
                  <th className="py-3 px-3.5 text-right">Total price</th>
                  <th className="py-3 px-3.5">Insurance end date</th>
                  <th className="py-3 px-3.5 text-center">Status</th>
                  <th className="py-3 pr-4 pl-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eaeeef]">
                {paginatedAssets.map((asset, idx) => {
                  const totalPrice =
                    (Number(asset.unitPrice) +
                      (Number(asset.unitPrice) * Number(asset.taxRate)) / 100) *
                    Number(asset.qty ?? 1);

                  const st = asset.status || "Pending";
                  const isDelivered = String(st).toLowerCase() === "delivered";

                  return (
                    <tr
                      key={asset.id || idx}
                      className="hover:bg-[#f1f7f8]/50 transition-colors text-[13px]"
                    >
                      {/* Asset ID Column */}
                      <td className="py-3 px-3.5 pl-4">
                        <div className="flex items-center gap-2 font-mono text-[12px] font-semibold text-[#2e6b76]">
                          <div className="w-7 h-7 rounded-[7px] bg-[#F1F6FA] text-[#2e6b76] flex items-center justify-center flex-shrink-0">
                            <Smartphone className="w-3.5 h-3.5" />
                          </div>
                          <span>{asset.assetId || "—"}</span>
                        </div>
                      </td>

                      {/* Company Name */}
                      <td className="py-3 px-3.5 text-[#526066]">
                        {asset.entity_name?.trim() || "—"}
                      </td>

                      {/* Asset Name */}
                      <td className="py-3 px-3.5">
                        <div className="font-semibold text-[#151b1d]">
                          {asset.assetName || "—"}
                        </div>
                        {asset.specification && (
                          <div className="text-[11px] text-[#7c8b91] truncate max-w-[200px]">
                            {asset.specification}
                          </div>
                        )}
                      </td>

                      {/* Qty */}
                      <td className="py-3 px-3.5 text-right font-medium text-[#151b1d] tabular-nums">
                        {asset.qty ?? 1}
                      </td>

                      {/* Total Price */}
                      <td className="py-3 px-3.5 text-right font-semibold text-[#151b1d] tabular-nums whitespace-nowrap">
                        {inr(totalPrice)}
                      </td>

                      {/* Insurance End Date */}
                      <td className="py-3 px-3.5 text-[#7c8b91] whitespace-nowrap">
                        {asset.insuranceEndDate
                          ? new Date(asset.insuranceEndDate).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "—"}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-3.5 text-center whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-[6px] text-[11px] font-semibold tracking-wide uppercase ${
                            isDelivered
                              ? "bg-[#e1f0f1] text-[#2e6b76]"
                              : "bg-[#f3e7c9] text-[#8c6500]"
                          }`}
                        >
                          {isDelivered ? "Delivered" : "Pending"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3 pr-4 pl-3 text-right">
                        <button
                          type="button"
                          onClick={() => void handleAssetViewDetails(asset)}
                          className="w-7 h-7 rounded-[6px] text-[#7c8b91] hover:text-[#3f8b98] hover:bg-[#e1f0f1] inline-flex items-center justify-center transition-colors cursor-pointer"
                          title="View asset"
                          aria-label="View asset"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between border-t border-[#dde3e4] px-4 py-3 bg-white text-xs text-[#526066]">
            <div>
              {assets.length > 0
                ? `Showing ${startIndex + 1}–${Math.min(startIndex + PAGE_SIZE, assets.length)} of ${assets.length}`
                : "Showing 0 of 0"}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                >
                  ‹
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  const isActive = currentPage === pageNum;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-[28px] h-7 px-2 rounded-[6px] text-xs font-medium cursor-pointer transition-colors ${
                        isActive
                          ? "bg-[#3f8b98] border border-[#3f8b98] text-white"
                          : "bg-white border border-[#dde3e4] text-[#151b1d] hover:border-[#3f8b98]"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-[#7c8b91]">
          <Package className="w-10 h-10 opacity-30 mb-2" />
          <p className="text-sm font-semibold text-[#151b1d]">No assets found</p>
          <p className="text-xs text-[#7c8b91] mt-0.5">Assets supplied via purchase orders will appear here</p>
        </div>
      )}

      {/* Asset Detail Sidebar */}
      {selectedAsset && (
        <AssetDetailSidebar
          open={assetOpen}
          onOpenChange={setAssetOpen}
          asset={selectedAsset}
          po={selectedPo}
          supplier={supplier}
          customer={customer}
          isIndividualAsset={true}
          assetsCategories={assetsCategories}
          invoices={invoices}
          updateDetails={handleUpdateAssetDetails}
          onPrimaryImageChange={handlePrimaryImageChange}
        />
      )}
    </div>
  );
}
